/**
 * Pagination footer for a single dashboard lane.
 *
 * Deliberately generic: `totalCount` is always supplied by the caller from
 * the index/count query (see the architectural invariant in
 * `src/lib/crm/dashboard.ts`), never derived from the loaded page itself, so
 * "Showing X–Y of Z" can never understate a lane's real size.
 */

import { Button } from '@/components/ui/button';

interface PaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export function Pagination({
  page,
  pageSize,
  totalCount,
  onPageChange,
}: PaginationProps): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalCount);
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  const handlePrevious = (): void => {
    if (hasPrevious) onPageChange(currentPage - 1);
  };

  const handleNext = (): void => {
    if (hasNext) onPageChange(currentPage + 1);
  };

  return (
    <nav className="flex items-center justify-between gap-4" aria-label="Pagination">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
        aria-disabled={!hasPrevious}
        onClick={handlePrevious}
      >
        Previous
      </Button>
      <p role="status" className="text-sm text-muted-foreground">
        Showing {rangeStart}–{rangeEnd} of {totalCount}
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
        aria-disabled={!hasNext}
        onClick={handleNext}
      >
        Next
      </Button>
    </nav>
  );
}
